import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { api } from "@/api";
import logoUrl from "@/assets/ankhara-logo.webp";
import NotifyCapture from "@/components/NotifyCapture";
import { SITE } from "@/config/site";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";

const track = (name, props = {}) => {
  try { window.posthog?.capture(name, props); } catch { /* noop */ }
  try { window.gtag?.("event", name, props); } catch { /* noop */ }
};

export default function Archive() {
  const [photos, setPhotos] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState("");
  const [lightbox, setLightbox] = useState(null); // index into grid
  const [lbLoaded, setLbLoaded] = useState(false);
  const touchStart = useRef(null);
  const lastWheel = useRef(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/photo-sessions").then(({ data }) => setSessions(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/photos", { params: session ? { session } : {} })
      .then(({ data }) => setPhotos(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    track("archive_view", { session: session || "all" });
  }, [session]);

  const hero = useMemo(() => photos.find((p) => p.is_hero), [photos]);
  const grid = useMemo(() => photos.filter((p) => !p.is_hero), [photos]);

  const download = async (photo) => {
    try {
      const { data } = await api.post(`/photos/${photo.id}/download`);
      track("photo_download", { session: photo.session, photo_id: photo.id });
      const a = document.createElement("a");
      a.href = data.url;
      a.download = photo.original_name || "ankhara-photo.jpg";
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setPhotos((ps) => ps.map((p) => (p.id === photo.id ? { ...p, downloads: data.downloads } : p)));
    } catch { /* noop */ }
  };

  const move = useCallback(
    (dir) => {
      setLightbox((cur) => (cur === null ? null : (cur + dir + grid.length) % grid.length));
    },
    [grid.length]
  );

  // preload the open photo's neighbors (and theirs) so arrow/swipe nav is instant
  useEffect(() => {
    if (lightbox === null || grid.length === 0) return;
    setLbLoaded(false);
    [lightbox - 2, lightbox - 1, lightbox + 1, lightbox + 2].forEach((i) => {
      const p = grid[(i + grid.length) % grid.length];
      if (p) {
        const img = new window.Image();
        img.src = p.display_url || p.file_url;
      }
    });
  }, [lightbox, grid]);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    touchStart.current = null;
    // horizontal swipe only — steep vertical gestures keep scrolling semantics
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      move(dx < 0 ? 1 : -1);
    }
  };

  const onWheel = (e) => {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || Math.abs(e.deltaX) < 30) return;
    const now = Date.now();
    if (now - lastWheel.current < 350) return;
    lastWheel.current = now;
    move(e.deltaX > 0 ? 1 : -1);
  };

  const imgFallback = (e, p) => {
    const el = e.currentTarget;
    if (!el.dataset.fb) {
      el.dataset.fb = "1";
      el.src = p.display_url || p.file_url;
    } else if (el.dataset.fb === "1" && el.src !== (p.file_url || "")) {
      el.dataset.fb = "2";
      el.src = p.file_url;
    }
  };

  useEffect(() => {
    if (lightbox === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowLeft") move(-1);
      if (e.key === "ArrowRight") move(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, move]);

  const current = lightbox !== null ? grid[lightbox] : null;

  return (
    <div className="arc-page" data-testid="archive-page">
      <div className="com-noise" aria-hidden="true" />
      <header className="com-head">
        <div className="com-head-in">
          <a href="/" className="arc-logo-link" data-testid="archive-home-link">
            <img src={logoUrl} alt="ANKHARA" className="com-logo" />
          </a>
          <span className="com-title" data-testid="archive-title">THE ARCHIVE</span>
          <nav className="arc-nav">
            <a href="/community" className="ank-nav-link" data-testid="archive-community-link">THE FEED</a>
            <a href={SITE.instagramUrl} target="_blank" rel="noopener noreferrer" className="ank-nav-link" data-testid="archive-instagram-link">INSTAGRAM ↗</a>
          </nav>
        </div>
        {sessions.length > 0 && (
          <div className="com-sessions" data-testid="session-filter">
            <button type="button" className={`ank-chip ${session === "" ? "on" : ""}`}
              onClick={() => setSession("")} data-testid="session-chip-all">ALL</button>
            {sessions.map((s) => (
              <button key={s} type="button" className={`ank-chip ${session === s ? "on" : ""}`}
                onClick={() => setSession(s)} data-testid={`session-chip-${s}`}>SESSION {s}</button>
            ))}
          </div>
        )}
      </header>

      <main className="arc-main">
        {loading ? (
          <p className="com-loader" data-testid="archive-loading">DEVELOPING…</p>
        ) : photos.length === 0 ? (
          <div className="com-empty" data-testid="archive-empty">
            <p className="com-empty-title">NOTHING DEVELOPED YET</p>
            <p className="com-empty-sub">Session photos land here after the night is processed.</p>
          </div>
        ) : (
          <>
            {hero && (
              <figure className="arc-hero" data-testid="archive-hero">
                <img src={hero.display_url || hero.thumb_url} alt={`ANKHARA session ${hero.session} cover`}
                  onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = "1"; e.currentTarget.src = hero.file_url; } }} />
                <figcaption>
                  <span>SESSION {hero.session}</span>
                  Photos by <a href="https://instagram.com/stewgives" target="_blank" rel="noopener noreferrer" data-testid="photographer-credit">@stewgives</a>
                </figcaption>
              </figure>
            )}
            <div className="arc-grid" data-testid="photo-grid">
              {grid.map((p, i) => (
                <figure key={p.id} className="arc-card" data-testid={`photo-${p.id}`}>
                  <button type="button" className="arc-card-img" onClick={() => setLightbox(i)}
                    aria-label={`Open photo ${p.original_name || p.id}`}>
                    <img src={p.thumb_url} alt={`ANKHARA session ${p.session}`} loading="lazy"
                      onError={(e) => imgFallback(e, p)} />
                  </button>
                  <figcaption className="arc-card-meta">
                    <button type="button" className="arc-dl" onClick={() => download(p)}
                      aria-label="Download photo" data-testid={`download-${p.id}`}>
                      <Download size={14} />
                    </button>
                    <span className="arc-count" data-testid={`count-${p.id}`}>
                      {p.downloads || 0} DL
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </>
        )}
      </main>

      {current && (
        <div className="arc-lightbox" data-testid="lightbox" onClick={() => setLightbox(null)}
          onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onWheel={onWheel}>
          <button type="button" className="arc-lb-close" onClick={() => setLightbox(null)} aria-label="Close" data-testid="lightbox-close"><X size={22} /></button>
          <button type="button" className="arc-lb-arrow left" onClick={(e) => { e.stopPropagation(); move(-1); }} aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft size={30} /></button>
          <div className="arc-lb-stage" onClick={(e) => e.stopPropagation()}
            style={current.width && current.height ? {
              width: Math.min(window.innerWidth * 0.92, window.innerHeight * 0.82 * (current.width / current.height)),
              aspectRatio: `${current.width} / ${current.height}`,
            } : undefined}>
            <img src={current.thumb_url} alt="" aria-hidden="true" className="arc-lb-placeholder" />
            <img
              key={current.id}
              src={current.display_url || current.file_url}
              alt={`ANKHARA session ${current.session} full`}
              className={`arc-lb-img ${lbLoaded ? "in" : ""}`}
              data-testid="lightbox-img"
              onLoad={() => setLbLoaded(true)}
            />
          </div>
          <button type="button" className="arc-lb-arrow right" onClick={(e) => { e.stopPropagation(); move(1); }} aria-label="Next" data-testid="lightbox-next"><ChevronRight size={30} /></button>
          <div className="arc-lb-bar" onClick={(e) => e.stopPropagation()}>
            <span className="arc-lb-count">{lightbox + 1} / {grid.length}</span>
            <button type="button" className="pop-cta arc-lb-dl" onClick={() => download(current)} data-testid="lightbox-download">
              <Download size={15} style={{ marginRight: 6, verticalAlign: -2 }} />DOWNLOAD FULL QUALITY
            </button>
          </div>
          <div className="arc-lb-notify" onClick={(e) => e.stopPropagation()}>
            <NotifyCapture source="archive_lightbox" testid="lb-notify" />
          </div>
        </div>
      )}
    </div>
  );
}
